/**
 * Custom Three.js 3D Model Viewer for Shopify
 * This bypasses Shopify's CDN compression for better texture quality
 *
 * Usage:
 * <threejs-model-viewer
 *   src="https://your-cdn.com/model.glb"
 *   poster="https://your-cdn.com/poster.jpg"
 *   camera-controls
 *   auto-rotate>
 * </threejs-model-viewer>
 */

class ThreeJSModelViewer extends HTMLElement {
  constructor() {
    super();
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.model = null;
    this.mixer = null;
    this.clock = null;
    this.isLoaded = false;
    this.isInteracting = false;
  }

  static get observedAttributes() {
    return ['src', 'poster', 'camera-controls', 'auto-rotate', 'environment-image'];
  }

  connectedCallback() {
    console.log('[ThreeJS Viewer] Connected, src:', this.getAttribute('src'));
    this.setupStyles();
    this.setupContainer();

    // Lazy load - only initialize when visible
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              console.log('[ThreeJS Viewer] Element visible, loading dependencies...');
              this.loadDependencies();
              observer.unobserve(this);
            }
          });
        },
        { rootMargin: '100px' }
      );
      observer.observe(this);
    } else {
      this.loadDependencies();
    }
  }

  disconnectedCallback() {
    this.dispose();
  }

  setupStyles() {
    this.style.display = 'block';
    this.style.position = 'relative';
    this.style.width = '100%';
    this.style.height = '100%';
    this.style.minHeight = '400px';
    this.style.aspectRatio = '1 / 1';
  }

  setupContainer() {
    // Create canvas container
    this.container = document.createElement('div');
    this.container.style.cssText = `
      width: 100%;
      height: 100%;
      min-height: 400px;
      position: absolute;
      top: 0;
      left: 0;
      overflow: hidden;
      background: transparent;
    `;

    // Create poster image if provided
    const posterUrl = this.getAttribute('poster');
    if (posterUrl) {
      this.poster = document.createElement('img');
      this.poster.src = posterUrl;
      this.poster.alt = 'Loading 3D model...';
      this.poster.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        z-index: 1;
        transition: opacity 0.3s ease;
      `;
      this.container.appendChild(this.poster);
    }

    // Create loading indicator
    this.loader = document.createElement('div');
    this.loader.innerHTML = `
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        z-index: 2;
      ">
        <div style="
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #333;
          border-radius: 50%;
          animation: threejs-spin 1s linear infinite;
          margin: 0 auto 10px;
        "></div>
        <span style="color: #666; font-size: 14px;">Loading 3D Model...</span>
      </div>
    `;
    this.loader.style.display = 'none';
    this.container.appendChild(this.loader);

    // Add animation keyframes and control styles
    if (!document.getElementById('threejs-viewer-styles')) {
      const style = document.createElement('style');
      style.id = 'threejs-viewer-styles';
      style.textContent = `
        @keyframes threejs-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        threejs-model-viewer canvas {
          display: block;
          width: 100% !important;
          height: 100% !important;
        }
        .threejs-controls {
          position: absolute;
          bottom: 16px;
          right: 16px;
          display: flex;
          gap: 8px;
          z-index: 10;
        }
        .threejs-controls button {
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          color: #333;
          font-size: 20px;
          font-weight: bold;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          transition: all 0.2s ease;
        }
        .threejs-controls button:hover {
          background: #fff;
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
        .threejs-controls button:active {
          transform: scale(0.95);
        }
        .threejs-controls button svg {
          width: 20px;
          height: 20px;
          fill: currentColor;
        }
        threejs-model-viewer.fullscreen {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          z-index: 9999 !important;
          background: #f5f5f5 !important;
        }
        threejs-model-viewer.fullscreen .threejs-controls {
          bottom: 24px;
          right: 24px;
        }
      `;
      document.head.appendChild(style);
    }

    // Create control buttons
    this.createControls();

    this.appendChild(this.container);
  }

  createControls() {
    this.controlsContainer = document.createElement('div');
    this.controlsContainer.className = 'threejs-controls';

    // Zoom In button
    const zoomInBtn = document.createElement('button');
    zoomInBtn.innerHTML = '+';
    zoomInBtn.title = 'Zoom In';
    zoomInBtn.addEventListener('click', () => this.zoom(0.8));

    // Zoom Out button
    const zoomOutBtn = document.createElement('button');
    zoomOutBtn.innerHTML = '−';
    zoomOutBtn.title = 'Zoom Out';
    zoomOutBtn.addEventListener('click', () => this.zoom(1.2));

    // Fullscreen button
    const fullscreenBtn = document.createElement('button');
    fullscreenBtn.innerHTML = `<svg viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>`;
    fullscreenBtn.title = 'Fullscreen';
    fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());

    this.controlsContainer.appendChild(zoomInBtn);
    this.controlsContainer.appendChild(zoomOutBtn);
    this.controlsContainer.appendChild(fullscreenBtn);

    this.container.appendChild(this.controlsContainer);
  }

  zoom(factor) {
    if (this.camera) {
      this.camera.position.multiplyScalar(factor);
      if (this.controls) {
        this.controls.update();
      }
    }
  }

  toggleFullscreen() {
    if (this.classList.contains('fullscreen')) {
      this.classList.remove('fullscreen');
      document.body.style.overflow = '';
    } else {
      this.classList.add('fullscreen');
      document.body.style.overflow = 'hidden';
    }
    // Trigger resize to update renderer
    setTimeout(() => this.onResize(), 100);
  }

  async loadDependencies() {
    this.loader.style.display = 'block';
    console.log('[ThreeJS Viewer] Starting to load dependencies...');

    try {
      // Load Three.js from CDN if not already loaded (using r128 which has stable UMD builds)
      if (!window.THREE) {
        console.log('[ThreeJS Viewer] Loading Three.js...');
        await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js');
        console.log('[ThreeJS Viewer] Three.js loaded');
      }

      // Load GLTFLoader
      if (!window.THREE.GLTFLoader) {
        console.log('[ThreeJS Viewer] Loading GLTFLoader...');
        await this.loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js');
        console.log('[ThreeJS Viewer] GLTFLoader loaded');
      }

      // Load DRACOLoader for compressed models
      if (!window.THREE.DRACOLoader) {
        console.log('[ThreeJS Viewer] Loading DRACOLoader...');
        await this.loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/DRACOLoader.js');
        console.log('[ThreeJS Viewer] DRACOLoader loaded');
      }

      // Load OrbitControls
      if (!window.THREE.OrbitControls) {
        console.log('[ThreeJS Viewer] Loading OrbitControls...');
        await this.loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js');
        console.log('[ThreeJS Viewer] OrbitControls loaded');
      }

      // Load RGBELoader for HDR environment (optional)
      if (!window.THREE.RGBELoader) {
        console.log('[ThreeJS Viewer] Loading RGBELoader...');
        await this.loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/RGBELoader.js');
        console.log('[ThreeJS Viewer] RGBELoader loaded');
      }

      console.log('[ThreeJS Viewer] All dependencies loaded, initializing...');
      this.initThreeJS();
      this.loadModel();
    } catch (error) {
      console.error('[ThreeJS Viewer] Failed to load dependencies:', error);
      this.showError('Failed to load 3D viewer');
    }
  }

  showError(message) {
    this.loader.innerHTML = `
      <div style="color: #c00; font-size: 14px;">${message}</div>
    `;
  }

  loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error(`Failed to load: ${src}`)));
        }
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = () => reject(new Error(`Failed to load: ${src}`));
      document.head.appendChild(script);
    });
  }

  initThreeJS() {
    const THREE = window.THREE;

    // Scene
    this.scene = new THREE.Scene();
    this.scene.background = null; // Transparent background

    // Camera
    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
    this.camera.position.set(0, 1, 3);

    // Get exposure from attribute (default: 1.0)
    const exposureValue = parseFloat(this.getAttribute('exposure')) || 1.0;

    // Renderer with high quality settings
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false, // Use solid background for better contrast
      powerPreference: 'high-performance',
      preserveDrawingBuffer: true,
    });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputEncoding = THREE.sRGBEncoding; // r128 uses encoding instead of colorSpace
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = exposureValue; // Use exposure from settings
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.physicallyCorrectLights = true; // Better light calculations

    // Subtle light gray background
    this.renderer.setClearColor(0xf5f5f5, 1);

    this.container.appendChild(this.renderer.domElement);

    // Controls
    if (this.hasAttribute('camera-controls')) {
      this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.screenSpacePanning = true;
      this.controls.minDistance = 0.5;
      this.controls.maxDistance = 10;
      this.controls.maxPolarAngle = Math.PI;
      this.controls.target.set(0, 0, 0);

      // Track interaction for auto-rotate pause
      this.controls.addEventListener('start', () => {
        this.isInteracting = true;
      });
      this.controls.addEventListener('end', () => {
        this.isInteracting = false;
      });
    }

    // Lighting
    this.setupLighting();

    // Clock for animations
    this.clock = new THREE.Clock();

    // Handle resize
    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.container);

    // Start render loop
    this.animate();
  }

  setupLighting() {
    const THREE = window.THREE;

    // Soft ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    // Main directional light (key light)
    const mainLight = new THREE.DirectionalLight(0xffffff, 0.8);
    mainLight.position.set(5, 10, 7.5);
    this.scene.add(mainLight);

    // Fill light from the opposite side
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
    fillLight.position.set(-5, 5, -5);
    this.scene.add(fillLight);

    // Back light for subtle rim lighting
    const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
    backLight.position.set(0, 5, -10);
    this.scene.add(backLight);

    // Front light
    const frontLight = new THREE.DirectionalLight(0xffffff, 0.5);
    frontLight.position.set(0, 2, 10);
    this.scene.add(frontLight);

    // Hemisphere light for natural ambient
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xe8e8e8, 0.5);
    hemiLight.position.set(0, 20, 0);
    this.scene.add(hemiLight);

    // Load environment map if specified
    const envImage = this.getAttribute('environment-image');
    if (envImage) {
      this.loadEnvironment(envImage);
    }
  }

  loadEnvironment(url) {
    const THREE = window.THREE;

    if (url.endsWith('.hdr')) {
      const rgbeLoader = new THREE.RGBELoader();
      rgbeLoader.load(url, (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        this.scene.environment = texture;
      });
    }
  }

  loadModel() {
    const THREE = window.THREE;
    let src = this.getAttribute('src');

    if (!src) {
      console.error('[ThreeJS Viewer] No src attribute provided');
      this.loader.style.display = 'none';
      return;
    }

    // Ensure absolute URL
    if (src.startsWith('//')) {
      src = 'https:' + src;
    } else if (src.startsWith('/')) {
      src = window.location.origin + src;
    }

    console.log('[ThreeJS Viewer] Loading model from:', src);

    const loader = new THREE.GLTFLoader();

    // Setup DRACO decoder for compressed models
    const dracoLoader = new THREE.DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.5/');
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      src,
      (gltf) => {
        console.log('[ThreeJS Viewer] Model loaded successfully');
        this.model = gltf.scene;

        // Apply high-quality texture settings and boost brightness for dark objects
        this.model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;

            if (child.material) {
              const materials = Array.isArray(child.material) ? child.material : [child.material];

              materials.forEach((material) => {
                // Enable high-quality rendering
                material.needsUpdate = true;

                // Slight material adjustment for dark objects
                if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
                  // Slightly reduce roughness for subtle reflections
                  if (material.roughness > 0.8) {
                    material.roughness = Math.max(0.6, material.roughness - 0.1);
                  }
                }

                // Improve texture quality
                const textureMaps = [
                  'map',
                  'normalMap',
                  'roughnessMap',
                  'metalnessMap',
                  'aoMap',
                  'emissiveMap',
                  'bumpMap',
                  'displacementMap',
                ];

                textureMaps.forEach((mapName) => {
                  const texture = material[mapName];
                  if (texture) {
                    // Maximum anisotropic filtering for sharp textures
                    texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();

                    // High quality filtering
                    texture.minFilter = THREE.LinearMipmapLinearFilter;
                    texture.magFilter = THREE.LinearFilter;

                    // Generate mipmaps for better quality at distance
                    texture.generateMipmaps = true;

                    // Ensure proper color space (r128 uses encoding, not colorSpace)
                    if (mapName === 'map' || mapName === 'emissiveMap') {
                      texture.encoding = THREE.sRGBEncoding;
                    }

                    texture.needsUpdate = true;
                  }
                });
              });
            }
          }
        });

        // Center and scale model
        const box = new THREE.Box3().setFromObject(this.model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        this.model.scale.setScalar(scale);

        this.model.position.sub(center.multiplyScalar(scale));
        this.model.position.y -= box.min.y * scale;

        this.scene.add(this.model);

        // Setup animations if present
        if (gltf.animations && gltf.animations.length > 0) {
          this.mixer = new THREE.AnimationMixer(this.model);
          gltf.animations.forEach((clip) => {
            this.mixer.clipAction(clip).play();
          });
        }

        // Update camera position based on model size
        this.camera.position.set(0, size.y * scale * 0.5, maxDim * scale * 2.5);
        if (this.controls) {
          this.controls.target.set(0, size.y * scale * 0.3, 0);
          this.controls.update();
        }

        // Hide loader and poster
        this.loader.style.display = 'none';
        if (this.poster) {
          this.poster.style.opacity = '0';
          setTimeout(() => {
            this.poster.style.display = 'none';
          }, 300);
        }

        this.isLoaded = true;
        this.dispatchEvent(new CustomEvent('load', { detail: { model: this.model } }));
      },
      (progress) => {
        // Progress callback
        if (progress.total > 0) {
          const percent = Math.round((progress.loaded / progress.total) * 100);
          const loadingText = this.loader.querySelector('span');
          if (loadingText) {
            loadingText.textContent = `Loading... ${percent}%`;
          }
        }
      },
      (error) => {
        console.error('[ThreeJS Viewer] Error loading 3D model:', error);
        this.loader.innerHTML = `
          <div style="text-align: center; color: #cc0000;">
            <p>Failed to load 3D model</p>
            <small>${error.message || 'Unknown error'}</small>
          </div>
        `;
        this.dispatchEvent(new CustomEvent('error', { detail: { error } }));
      }
    );
  }

  animate() {
    if (!this.renderer) return;

    requestAnimationFrame(() => this.animate());

    const delta = this.clock.getDelta();

    // Update animations
    if (this.mixer) {
      this.mixer.update(delta);
    }

    // Auto-rotate
    if (this.hasAttribute('auto-rotate') && this.model && !this.isInteracting) {
      this.model.rotation.y += 0.005;
    }

    // Update controls
    if (this.controls) {
      this.controls.update();
    }

    this.renderer.render(this.scene, this.camera);
  }

  onResize() {
    if (!this.camera || !this.renderer) return;

    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
  }

  dispose() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.controls) {
      this.controls.dispose();
    }

    if (this.renderer) {
      this.renderer.dispose();
    }

    if (this.scene) {
      this.scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => {
            Object.keys(material).forEach((key) => {
              if (material[key] && material[key].dispose) {
                material[key].dispose();
              }
            });
            material.dispose();
          });
        }
      });
    }
  }

  // Public methods
  resetCamera() {
    if (this.controls) {
      this.controls.reset();
    }
  }

  setAutoRotate(enabled) {
    if (enabled) {
      this.setAttribute('auto-rotate', '');
    } else {
      this.removeAttribute('auto-rotate');
    }
  }
}

// Register the custom element
if (!customElements.get('threejs-model-viewer')) {
  customElements.define('threejs-model-viewer', ThreeJSModelViewer);
}
